/**
 * REV-797: the catalog of error codes the API returns in `error.code`.
 *
 * WHY THIS EXISTS
 * ---------------
 * The API's error messages are English sentences written for whoever reads the
 * response. A UI that translates those sentences is translating the wrong
 * thing, and stops working the moment one is reworded. The code is the stable
 * part, so the UI looks a code up here, shows its own translated text for it,
 * and shows a generic message for a code it does not know. It never renders
 * `error.message`.
 *
 * WHAT IS IN IT
 * -------------
 * Every code the API can emit, each with a one-line description for people who
 * integrate with the API. The description is documentation, not UI copy, and
 * it is English on purpose: the translated, user-facing text lives in
 * revenant-ui's locale files, one `errors.<CODE>` key per entry.
 *
 * Two kinds of code share the table:
 *   - the status defaults (`NOT_FOUND`, `VALIDATION_ERROR`, ...) that
 *     utils/apiError.js fills in when a call site names no code, and
 *   - domain codes (`CONTEXT_LIMIT_REACHED`, `not_found` from the audit services,
 *     ...) that a call site or a service names.
 * The lower-case ones are what the audit, scope and sitemap services have
 * always emitted. They are catalogued as they are because a client may already
 * branch on them; renaming a shipped code is a breaking change.
 *
 * THE GUARD
 * ---------
 * tests/unit/contracts/errorCodeCatalog.test.js reads the source for every code
 * it can emit and fails if one is missing here, and fails on a new
 * sendError / httpError call that names no code. revenant-ui's guard fails if
 * an entry here has no `errors.<CODE>` string.
 *
 * VERSIONING
 * ----------
 * `errorCatalogVersion` is bumped when a code is removed or its meaning
 * changes. Adding a code is not a bump: a client that does not know it shows
 * the generic message. The version is published in the OpenAPI document as
 * `info.x-error-catalog-version`, with the table itself as `x-error-codes`.
 *
 * Version 2 (REV-1130) removed SEAT_LIMIT_REACHED: no plan caps the number of
 * people in an organization.
 *
 * Not in the contract barrel and DATA ONLY, for the reasons in
 * ./chartTokens.js: the vendored copy is type-checked as strict TypeScript.
 */

const errorCatalogVersion = 2;

const errorCatalog = {
  // Status defaults (utils/apiError.js DEFAULT_CODES)
  VALIDATION_ERROR:
    'The request was malformed or a field failed validation (400). `error.details.fields` lists the field names that failed, when it can.',
  UNAUTHENTICATED: 'No valid session or credential was presented (401).',
  PAYMENT_REQUIRED: 'The action needs a plan or payment the organization does not have (402).',
  FORBIDDEN: 'The caller is authenticated but not allowed to do this (403).',
  NOT_FOUND: 'The resource does not exist, or the caller may not know that it does (404).',
  CONFLICT: 'The request conflicts with the current state of the resource (409).',
  GONE: 'The resource existed and has been removed (410).',
  UNPROCESSABLE: 'The request was understood but cannot be carried out (422).',
  LOCKED: 'The resource is locked and cannot be changed (423).',
  PAYLOAD_TOO_LARGE: 'The request body is larger than the API accepts (413).',
  RATE_LIMITED: 'Too many requests; retry after the interval in the response (429).',
  INTERNAL_ERROR: 'An unexpected server error. The requestId identifies it in the logs (500).',
  NOT_IMPLEMENTED: 'The endpoint exists but this deployment does not provide it (501).',
  BAD_GATEWAY: 'A service this API depends on answered badly (502).',
  SERVICE_UNAVAILABLE: 'A service this API depends on is unavailable; retry later (503).',
  ERROR: 'A status with no more specific code.',

  // Sign-in and access
  INVALID_CREDENTIALS: 'The email or password is wrong.',
  ACCOUNT_LOCKED: 'The account is temporarily locked after repeated failed sign-ins.',
  TEAM_SLUG_TAKEN: 'The organization already has a team with this slug.',
  ORGANIZATION_SLUG_TAKEN: 'An organization with this slug already exists.',
  ROLE_REQUIRED:
    'The action needs a higher organization role. `error.details.requiredRole` is the lowest role that may do it.',
  OWNERSHIP_TRANSFER_REQUIRED:
    'An organization has exactly one owner, and only POST /organizations/{orgId}/transfer-ownership changes who it is: the owner role cannot be assigned, given by invitation, or taken away from the owner (409).',
  // Member actions: remove, deactivate, reactivate and transfer-ownership
  // (REV-1170). The specific reason a member action was refused, so the UI can
  // say which one rather than a generic "could not do that".
  CANNOT_ACT_ON_SELF:
    'The action cannot be taken on your own membership (removing or deactivating yourself).',
  CANNOT_ACT_ON_HIGHER_PRIVILEGE:
    'The target member has a role equal to or higher than yours, so you cannot remove or deactivate them.',
  NOT_A_MEMBER: 'The named user is not a member of this organization.',
  MEMBER_ALREADY_SUSPENDED: 'The member is already suspended, so there is nothing to deactivate.',
  MEMBER_ALREADY_ACTIVE: 'The member is already active, so there is nothing to reactivate.',
  NEW_OWNER_NOT_A_MEMBER:
    'Ownership can only be transferred to an active member of the organization, and the named user is not one.',
  ALREADY_OWNER: 'The named user is already the owner, so there is nothing to transfer.',
  ALREADY_ORG_MEMBER: 'The person invited is already an active member of the organization.',
  ACCEPTOR_ALREADY_MEMBER:
    'The person accepting an invitation already belongs to the organization.',
  INVITATION_ALREADY_PENDING: 'An invitation to this email address is already waiting.',
  ALREADY_TEAM_MEMBER: 'The person is already a member of the team.',
  ACCOUNT_NOT_ACTIVE: 'The user account is not active.',
  OAUTH_DISABLED: 'Sign-in with an external provider is switched off.',
  INSUFFICIENT_SCOPE: 'The API key lacks the scope this endpoint requires.',
  SERVICE_AUTH_NOT_CONFIGURED: 'Service-to-service authentication is not configured here.',
  SYSTEM_ROLES_NOT_SEEDED:
    'The installation has no system roles yet, so no organization can be made.',
  SYSTEM_ROLE_ROW_UNUSABLE: 'A system role exists but cannot be used to create an organization.',

  // Plans and billing
  CONTEXT_LIMIT_REACHED:
    "The organization's plan has no room for another context. `error.details` has `used`, `limit` and `plan`.",
  PLAN_NOT_PURCHASABLE: 'The plan cannot be bought through self-serve checkout.',
  NO_BILLING_ACCOUNT: 'The organization has no billing account yet.',

  // Contexts, scans, ingestion and discovery
  CONTEXT_NOT_FOUND: 'The context does not exist in this organization.',
  CONTEXT_NOT_ACTIVE: 'The context is not active, so it cannot take results.',
  CONTEXT_NOT_SCANNABLE:
    'The context is not a website or deployment, so it has no pages to rescan (hinted rescan, 422).',
  RESCAN_URL_REFUSED:
    "A hinted-rescan URL is not a page of the context's site, or was refused by the outbound URL check (422). `error.details.index` is its position; `error.details.reason` is one of outside_context, invalid_format, scheme, private_network. The URL itself is never echoed.",
  URL_OUTSIDE_CONTEXT:
    "The URL is not a page of the context's site, so it cannot be tested, imported or recorded for that context (422). The message names the site; `error.details.reason` is outside_context. The URL itself is never echoed.",
  INVALID_PAYLOAD: 'The submitted payload is not valid for this endpoint.',
  INVALID_SOURCE: 'The ingestion source type is not one the API accepts.',
  INVALID_TEST_CONFIG: 'The test configuration failed validation.',
  INVALID_WEBHOOK_TARGET:
    'The callback URL is not allowed. `error.details.reason` is one of invalid_format, scheme, credentials_in_url, not_https, private_network, unresolvable, not_a_list, too_many. For an ad-hoc test or a saved test configuration, `error.details.index` is the position of the first refused callback URL, when one URL was at fault.',
  NO_ACTIVE_TARGETS: 'The context has no active targets to scan.',
  NO_THUMBNAIL: 'No screenshot thumbnail exists for the finding.',
  MIGRATION_TOOL_NOT_FOUND: 'The tool named for migration is not known.',
  HANDOFF_FAILED: 'A page delivered by the crawler could not be accepted.',
  unknown_discovery_mode: 'The crawl discovery mode is not one the API supports.',
  spider_not_configured: 'The crawler service is not configured in this deployment.',
  url_rejected:
    'The start URL was refused by the outbound URL check. `error.details.reason` is one of invalid_format, scheme, private_network.',
  blocked_url: 'The sitemap address was refused by the outbound URL check.',
  bad_redirect: 'The sitemap redirected somewhere that is not allowed.',
  too_many_redirects: 'The sitemap redirected too many times.',
  http_error: 'The sitemap server answered with an error status.',
  timeout: 'The sitemap server did not answer in time.',
  unreachable: 'The sitemap server could not be reached.',
  too_large: 'The sitemap is larger than the importer accepts.',

  // Findings and feedback
  FINDING_ALREADY_CONFIRMED:
    'A confirmed finding cannot be marked a false positive until it is reopened.',
  FINDING_ALREADY_DISPOSITIONED:
    'A finding that already has a disposition cannot be confirmed until it is reopened.',
  FINDING_ALREADY_CLOSED:
    "A closed finding (resolved, false positive or won't fix) cannot be resolved or marked won't fix until it is reopened.",

  // Audits, evaluations and reports
  SNAPSHOT_NOT_FOUND: 'The conformance snapshot does not exist.',
  UNVERIFIED_IN_SNAPSHOT: 'The snapshot contains criteria that no one has verified.',
  unverified_in_snapshot:
    'The snapshot contains criteria that no one has verified. `error.details.count` is how many.',
  unverified_criteria:
    'The audit has criteria that no one has verified; locking needs them attested or a waiver. `error.details.count` is how many.',
  active_audit_exists: 'The context already has an active audit.',
  audit_locked: 'The audit is no longer in progress, so evaluations on it cannot be changed.',
  audit_not_found: 'The audit does not exist.',
  audit_not_in_progress: 'The audit is no longer in progress, so it cannot be updated.',
  not_in_progress: 'The audit is no longer in progress, so it cannot be updated.',
  not_finalized: 'The audit must be finalized before it can be locked.',
  context_not_active: 'The context is not active, so no audit can start.',
  context_not_found: 'The context does not exist in this organization.',
  criterion_not_found: 'The criterion does not exist in this audit.',
  immutable_fields: 'The request tried to change fields that cannot be changed.',
  invalid_attestation: 'The attestation is not valid.',
  invalid_verdict: 'The verdict is not one the API accepts.',
  invalid_population_key: 'The population key is not recognised.',
  invalid_population_value: 'The population value is not valid.',
  invalid_scope_type: 'The scope item type is not valid.',
  outside_scope_bounds:
    'The submitted identifier is not part of this tracked place. `error.details.allowed_origins` lists the origins it accepts; add the origin to the tracked place, send an address on one of them, or send a `scopeItemId`.',
  scope_item_not_in_context:
    'The `scopeItemId` is not a scope item of the tracked place the results were submitted for.',
  invalid_source: 'The scope item source is not valid.',
  invalid_argument: 'An argument has the wrong type or value.',
  invalid_url: 'The URL is not valid.',
  invalid_key: 'The API key does not look valid.',
  missing_context: 'The request names no context.',
  missing_identifier: 'The request names no identifier.',
  missing_messages: 'The AI request carries no messages.',
  missing_org: 'The request names no organization.',
  missing_purpose: 'The AI request states no purpose.',
  missing_user: 'The request names no user.',
  missing_waiver_justification:
    'Waiving the unverified-criteria check needs a justification of at least 20 characters.',
  not_found: 'The record does not exist in this organization.',
  parent_not_found: 'The parent audit does not exist.',

  // Account email change (REV-683)
  invalid_password: 'The current password is incorrect.',
  same_email: 'That is already your email address.',
  invalid_token: 'The link is invalid or has expired.',
  email_taken: 'That email address is already in use.',

  // AI features
  audit_write_failed: 'The AI request could not be recorded, so it was not made.',
  empty_response: 'The AI model returned nothing usable.',
  encryption_unavailable: 'Stored keys cannot be encrypted in this deployment.',
  no_credential: 'The organization has no AI key configured.',
  ai_disabled: 'AI features are turned off for this organization.',
  invalid_note_draft:
    "The AI draft named for this note is not this criterion's draft in this audit.",

  // Integrations: GitHub, Jira and the issue export
  token_required: 'A token is required to verify the connection.',
  github_token_invalid: 'GitHub rejected the token.',
  github_repo_inaccessible: 'The token cannot access the repository.',
  github_unavailable: 'GitHub is not responding normally, so the connection could not be verified.',
  github_unreachable: 'GitHub could not be reached.',
  github_create_failed: 'GitHub did not create the issue.',
  jira_auth_failed: 'Jira rejected the credential.',
  jira_unavailable: 'Jira is not responding normally, so the credential could not be verified.',
  jira_unreachable: 'Jira could not be reached.',
  jira_create_failed: 'Jira did not create the issue.',
  invalid_jira_config: 'The Jira connection is missing required settings.',
  connection_in_use: 'The connection is still used by tracked places and cannot be removed.',
  no_connection: 'The organization has no connection to the requested tracker.',
  ambiguous_connection: 'The organization has several connections to the tracker; name one.',
  provider_mismatch: 'The connection belongs to a different provider.',
  unsupported_provider: 'The integration provider is not supported.',
  invalid_repo_ref: 'The repository owner and name are not valid.',
  already_exported: 'The finding has already been exported to an issue.',

  // Crawler service
  spider_unreachable: 'The crawler service could not be reached.',
  spider_submit_failed: 'The crawler service refused to start the crawl.',
  spider_no_job_id: 'The crawler service started the crawl but returned no job id.',
  spider_job_read_failed: 'The crawler service could not report on the crawl.',
  spider_cancel_failed: 'The crawler service could not cancel the crawl.',
  crawl_url_required: 'A start URL is required to begin a crawl.',
  invalid_job_id: 'The crawl job id is not valid.',

  // AI provider failures (named for the provider SDK's error class)
  RateLimitError: 'The AI provider is rate-limiting requests.',
  AuthenticationError: "The AI provider rejected the organization's key.",
  PermissionDeniedError: "The AI provider refused the request for the organization's key.",
  BadRequestError: 'The AI provider rejected the request.',
  NotFoundError: 'The requested AI model is not available.',
  InternalServerError: 'The AI provider had an internal error.',
  TimeoutError: 'The AI provider did not respond in time.',
  APIError: 'The AI provider returned an error.',
  UnknownError: 'The AI request failed for a reason the API could not classify.',

  // Delivery from the crawler (service-to-service; no end user sees these)
  INVALID_DELIVERY: 'The delivered page failed validation.',
  URL_REJECTED:
    'The delivered page address was refused. `error.details.reason` is one of invalid_format, scheme, private_network.',
  SCAN_RUN_NOT_FOUND: 'The scan run is not one the caller may deliver to.',
  JOB_MISMATCH: 'The delivery names a different crawl job than the scan run.',
  ATTRIBUTION_MISMATCH: 'The delivery is attributed to a different organization.',
  SCAN_RUN_TERMINAL: 'The scan run has already finished.',
  DISCOVERY_CLOSED: 'The scan run no longer accepts discovered pages.',
  TARGET_WRITE_FAILED: 'The delivered page could not be stored.',
  PUBLISH_FAILED:
    'The delivered page, or every page of a hinted rescan, could not be queued; retry it.',
};

/**
 * Codes only a machine caller sees, so a UI needs no text for them.
 * Each is in `errorCatalog` too.
 */
const serviceOnlyErrorCodes = [
  'SERVICE_AUTH_NOT_CONFIGURED',
  'HANDOFF_FAILED',
  'INVALID_DELIVERY',
  'URL_REJECTED',
  'SCAN_RUN_NOT_FOUND',
  'JOB_MISMATCH',
  'ATTRIBUTION_MISMATCH',
  'SCAN_RUN_TERMINAL',
  'DISCOVERY_CLOSED',
  'TARGET_WRITE_FAILED',
  'PUBLISH_FAILED',
  // Hinted rescan (REV-737) is called by integrations, not the UI.
  'CONTEXT_NOT_SCANNABLE',
  'RESCAN_URL_REFUSED',
];

// Deliberately one line: see the note at the end of ./chartTokens.js.
export { errorCatalogVersion, errorCatalog, serviceOnlyErrorCodes };
